import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { UserCog, Plus, Pencil, Trash2, Shield, X, Save, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: 'super_admin', label: 'Super Admin', description: 'Pełen dostęp do wszystkich funkcji' },
  { value: 'admin', label: 'Administrator', description: 'Zarządzanie treścią i biznesem (bez użytkowników admin)' },
  { value: 'employee', label: 'Pracownik', description: 'Podgląd karnetów i rabatów, blog, hero, slider' },
  { value: 'translator', label: 'Tłumacz', description: 'Tylko dostęp do tłumaczeń' }
];

const AdminUsers = () => {
  const { getAuthHeader, admin: currentAdmin } = useAdminAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const emptyAdmin = {
    email: '',
    password: '',
    name: '',
    role: 'employee',
    isActive: true
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/users`, {
        headers: getAuthHeader()
      });
      setAdmins(response.data);
    } catch (error) {
      console.error('Failed to fetch admins:', error);
      toast.error('Nie udało się pobrać danych');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editingAdmin.email || !editingAdmin.name || !editingAdmin.role) {
      toast.error('Wypełnij wszystkie wymagane pola');
      return;
    }
    
    if (isCreating && !editingAdmin.password) {
      toast.error('Hasło jest wymagane dla nowego konta');
      return;
    }

    try {
      if (isCreating) {
        await axios.post(`${API}/api/admin/users`, editingAdmin, {
          headers: getAuthHeader()
        });
        toast.success('Administrator utworzony');
      } else {
        const updateData = { ...editingAdmin };
        if (!updateData.password) delete updateData.password; // Don't send empty password
        
        await axios.put(`${API}/api/admin/users/${editingAdmin.admin_id}`, updateData, {
          headers: getAuthHeader()
        });
        toast.success('Administrator zaktualizowany');
      }
      setEditingAdmin(null);
      setIsCreating(false);
      setShowPassword(false);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nie udało się zapisać');
    }
  };

  const handleDelete = async (adminId) => {
    if (!confirm('Czy na pewno chcesz usunąć tego administratora?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/users/${adminId}`, {
        headers: getAuthHeader()
      });
      toast.success('Administrator usunięty');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nie udało się usunąć');
    }
  };

  const getRoleInfo = (roleValue) => {
    return ROLES.find(r => r.value === roleValue) || { label: roleValue, description: '' };
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: 'bg-red-500/20 text-red-400',
      admin: 'bg-purple-500/20 text-purple-400',
      employee: 'bg-blue-500/20 text-blue-400',
      translator: 'bg-green-500/20 text-green-400'
    };
    return colors[role] || 'bg-gray-700 text-gray-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Administratorzy</h1>
          <p className="text-gray-400">Zarządzaj kontami administracyjnymi</p>
        </div>
        <Button
          onClick={() => { setEditingAdmin(emptyAdmin); setIsCreating(true); }}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nowy Admin
        </Button>
      </div>

      {/* Roles Overview */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-400" />
            Role i uprawnienia
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {ROLES.map(role => (
              <div key={role.value} className="p-3 bg-white/5 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 text-xs rounded ${getRoleBadgeColor(role.value)}`}>
                    {role.label}
                  </span>
                </div>
                <p className="text-gray-500 text-xs">{role.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Admins List */}
      <div className="space-y-3">
        {admins.map((adminUser) => {
          const roleInfo = getRoleInfo(adminUser.role);
          const isCurrentUser = adminUser.admin_id === currentAdmin?.admin_id;
          
          return (
            <Card key={adminUser.admin_id} className={`bg-white/5 border-white/10 ${!adminUser.isActive ? 'opacity-50' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-purple-400 font-bold text-lg">
                      {adminUser.name?.charAt(0) || 'A'}
                    </span>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-medium truncate">{adminUser.name}</h3>
                      {isCurrentUser && (
                        <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded">Ty</span>
                      )}
                      {!adminUser.isActive && (
                        <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">Nieaktywny</span>
                      )}
                    </div>
                    <p className="text-gray-400 text-sm truncate">{adminUser.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 text-xs rounded ${getRoleBadgeColor(adminUser.role)}`}>
                        {roleInfo.label}
                      </span>
                      {adminUser.lastLogin && (
                        <span className="text-gray-500 text-xs">
                          Ostatnie logowanie: {new Date(adminUser.lastLogin).toLocaleDateString('pl-PL')}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => { setEditingAdmin(adminUser); setIsCreating(false); }}
                      className="text-gray-400 hover:text-white"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    {!isCurrentUser && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(adminUser.admin_id)}
                        className="text-gray-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {admins.length === 0 && (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-8 text-center">
            <UserCog className="w-12 h-12 mx-auto text-gray-500 mb-4" />
            <p className="text-gray-400">Brak administratorów</p>
          </CardContent>
        </Card>
      )}

      {/* Edit Modal */}
      {editingAdmin && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-900 border-white/10 w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">
                {isCreating ? 'Nowy Administrator' : 'Edytuj Administratora'}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setEditingAdmin(null); setIsCreating(false); setShowPassword(false); }}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Name */}
              <div className="space-y-2">
                <Label className="text-gray-300">Imię i nazwisko *</Label>
                <Input
                  value={editingAdmin.name || ''}
                  onChange={(e) => setEditingAdmin({ ...editingAdmin, name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label className="text-gray-300">Email *</Label>
                <Input
                  type="email"
                  value={editingAdmin.email || ''}
                  onChange={(e) => setEditingAdmin({ ...editingAdmin, email: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  disabled={!isCreating}
                />
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label className="text-gray-300">
                  Hasło {isCreating ? '*' : '(pozostaw puste, aby nie zmieniać)'}
                </Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={editingAdmin.password || ''}
                    onChange={(e) => setEditingAdmin({ ...editingAdmin, password: e.target.value })}
                    className="bg-white/5 border-white/10 text-white pr-10"
                    placeholder={isCreating ? '' : '••••••••'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Role */}
              <div className="space-y-2">
                <Label className="text-gray-300">Rola *</Label>
                <Select
                  value={editingAdmin.role || 'employee'}
                  onValueChange={(value) => setEditingAdmin({ ...editingAdmin, role: value })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Active */}
              <div className="flex items-center gap-3">
                <Switch
                  checked={editingAdmin.isActive}
                  onCheckedChange={(checked) => setEditingAdmin({ ...editingAdmin, isActive: checked })}
                />
                <Label className="text-gray-300">Konto aktywne</Label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button variant="outline" onClick={() => { setEditingAdmin(null); setIsCreating(false); setShowPassword(false); }}>
                  Anuluj
                </Button>
                <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">
                  <Save className="w-4 h-4 mr-2" />
                  Zapisz
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
